import { FC, useState } from "react";
import { cn } from "@/lib/utils";
import Message from "../../../models/conversation/Message";
import MessageBubbleMarkdown from "./MessageBubbleMarkdown";
import { ChevronDown, ChevronUp } from "lucide-react";

type Props = {
  message: Message;
  bubbleTestId?: string;
  isOverview?: boolean;
};

const ChainOfThoughtBubble: FC<Props> = ({
  message,
  bubbleTestId,
  isOverview,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = () => {
    setIsExpanded(!isExpanded);
  };

  const thoughtProcessString =
    message.language === "en"
      ? "Thought process"
      : message.language === "sw"
        ? "Tankeprocess"
        : "Tankeproces";

  const revisedPromptString =
    message.language === "en"
      ? "Revised prompt"
      : message.language === "sw"
        ? "Omformulerad prompt"
        : "Omformuleret prompt";

  const getDescriptiveStepTitle = () => {
    // Finished assistant message: show completion, not the last streaming status (e.g. "Genererer svar").
    if (!message.human && message.isClosed) {
      return message.language === "en"
        ? "Response complete"
        : message.language === "sw"
          ? "Svar färdigt"
          : "Svar færdigt";
    }

    // First check if chainOfThoughtMessage is empty AND revisedPrompt is empty AND statusShort is empty
    if (
      !message.chainOfThoughtMessage &&
      !message.revisedPrompt &&
      !message.statusShort &&
      !message.statusLong
    ) {
      // If all are empty we need to return the default title in chosen language
      return message.language === "en"
        ? "Sending prompt to LLM"
        : message.language === "sw"
          ? "Skickar prompt till LLM"
          : "Sender prompt til LLM";
    }
    // If any of the three are not empty we need to return the statusShort in chosen language by the right hierarchy
    // 1. statusShort
    // 2. revisedPrompt
    // 3. chainOfThoughtMessage

    if (message.statusShort) {
      const messageStatusShortSplit = message.statusShort.split("\n");
      const lastStatusShort =
        messageStatusShortSplit[messageStatusShortSplit.length - 2];
      return lastStatusShort;
    }

    if (message.revisedPrompt) {
      return message.language === "en"
        ? "Revising your prompt"
        : message.language === "sw"
          ? "Reviderar din prompt"
          : "Reviderer din prompt";
    }

    if (message.chainOfThoughtMessage) {
      return message.language === "en"
        ? "Thinking through the prompt"
        : message.language === "sw"
          ? "Tänker igenom prompten"
          : "Tænker igennem prompten";
    }

    return "";
  };

  if (message.human) {
    return null;
  }

  return (
    <div className="flex flex-col items-start transition-all duration-75 ease-linear w-fit relative">
      <div
        className={cn(
          "rounded-lg px-3 py-2 text-sm relative",
          "bg-accent text-accent-foreground",
          "cursor-pointer transition-all duration-300",
          isExpanded ? "mb-2" : "-mb-2 z-30"
        )}
        data-testid={`${bubbleTestId}-thought`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs">{getDescriptiveStepTitle()}</span>
          <div className="ml-2">
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isExpanded
            ? "grid-rows-[1fr] opacity-100 h-full min-h-4"
            : "grid-rows-[0fr] opacity-0 min-h-4"
        )}
      >
        <div className="overflow-hidden border-l-2 px-4 ml-2 w-fit text-xs mb-4">
          {message.chainOfThoughtMessage && (
            <div className="mb-4">
              <div className="mb-1 font-bold w-fit">
                {thoughtProcessString}:
              </div>
              <MessageBubbleMarkdown
                message={{
                  ...message,
                  text: message.chainOfThoughtMessage,
                }}
                shouldMatchLawTerms={false}
                isOverview={isOverview}
              />
            </div>
          )}
          {message.revisedPrompt && (
            <div className="mb-4">
              <div className="mb-1 font-bold">{revisedPromptString}:</div>
              <MessageBubbleMarkdown
                message={{
                  ...message,
                  text: message.revisedPrompt || "",
                }}
                shouldMatchLawTerms={false}
                isOverview={isOverview}
              />
            </div>
          )}
          {message.statusLong && (
            <div>
              <MessageBubbleMarkdown
                message={{
                  ...message,
                  text: message.statusLong,
                }}
                isOverview={isOverview}
                shouldMatchLawTerms={false}
                componentsOverride={{
                  li: ({ ...props }) => (
                    <li className="my-1 text-xs" {...props} />
                  ),
                  ul: ({ ...props }) => (
                    <ul
                      className="list-disc pl-6 space-y-1 text-xs"
                      {...props}
                    />
                  ),
                  h2: ({ ...props }) => (
                    <h2
                      className="text-xs font-semibold mt-4 first:mt-0"
                      {...props}
                    />
                  ),
                  h3: ({ ...props }) => (
                    <h3
                      className="text-xs font-semibold mt-4 first:mt-0"
                      {...props}
                    />
                  ),
                  h4: ({ ...props }) => (
                    <h4
                      className="text-xs font-semibold mt-4 first:mt-0"
                      {...props}
                    />
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChainOfThoughtBubble;
